<template>
  <div class="pc-select-wrapper" :class="{disabled}">
    <input-label v-if="label" :disabled="disabled">
      {{ fieldLabel }}
    </input-label>
    <div
      v-click-outside="hideOptions"
      class="pc-select"
      :class="{top: showOptionsOnTop, opened: isOpened}"
      @click="toggleOptions"
    >
      <input
        v-if="searchable"
        v-model="searchUIText"
        :class="{ 'hidden': !(isOpened && searchable)}"
        class="pc-select__input pc-select__input-search"
        :disabled="disabled"
        @input="getfilteredOptions($event.target.value)"
      />
      <div v-show="!(isOpened && searchable)" class="pc-select__input">
        <p
          class="pc-select__input-text"
          :class="{'is-placeholder': !selectedUIText}"
        >
          {{ selectedUIText || placeholder }}
        </p>
      </div>
      <div v-if="icon === 'default'" class="pc-select__icon pc-select__arrow"></div>
      <icu-icon v-else :class="icon" class="pc-select__icon" />
      <div
        ref="options"
        class="pc-select-options"
        :class="{top: showOptionsOnTop, hidden: !optionsVisibility}"
        @click.stop
      >
        <div class="pc-select-options__container">
          <div v-if="!filteredOptions.length" class="not-found">
            {{ $t('notifications.not-found') }}
          </div>
          <div
            v-for="option in filteredOptions"
            :key="getOptionKey(option)"
            class="pc-select-options__option"
            :class="{selected: isOptionSelected(option)}"
            @click="selectOption(option)"
          >
            {{ getOptionLabel(option) }}
            <icu-icon class="pc-select-options__option-icon-selected pc-select-checked" />
          </div>
        </div>
      </div>
    </div>
    <input-error v-if="errorMessage">
      {{ $t(errorMessage) }}
    </input-error>
  </div>
</template>

<script>
import IcuIcon from '@/components/common/IcuIcon.vue'
import InputError from '@/components/inputs/InputError.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'

export default {
  name: 'PcSelect',
  components: {
    InputLabel,
    InputError,
    IcuIcon,
  },
  optionsMaxHeight: 200,
  props: {
    value: {
      type: [String, Number, Array],
      default: null
    },
    icon: {
      type: String,
      default: 'default'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    getOptionLabel: {
      type: Function,
      default: opt => opt.name
    },
    getOptionKey: {
      type: Function,
      default: opt => opt.id
    },
    getOptionValue: {
      type: Function,
      default: opt => opt?.id
    },
    placeholder: {
      type: String,
      default: ''
    },
    searchable:{
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      isOpened: false,
      searchUIText:'',
      filteredOptions:[],
      showOptionsOnTop: false,
      optionsVisibility: false
    }
  },
  computed: {
    selectedUIText() {
      if (this.multiple) {
        return this.selectedOptionsUIText
      } else {
        return this.selectedOption ? this.getOptionLabel(this.selectedOption) : ''
      }
    },
    selectedOption() {
      return this.options.find(option => this.getOptionValue(option) === this.value)
    },
    selectedOptions() {
      return this.options.filter(option => this.value?.includes(this.getOptionValue(option)))
    },
    selectedOptionsUIText() {
      return this.selectedOptions.map(opt => this.getOptionLabel(opt)).join(', ')
    },
    fieldLabel() {
      if (this.required) {
        return `${this.label} *`
      }

      return this.label
    },
    errorMessage() {
      return this.errors?.[0] || ''
    }
  },
  methods: {
    getfilteredOptions(value) {
      const searchStr = value?.toLowerCase()
      if(this.searchable && searchStr && searchStr.length > 1) {
        this.filteredOptions = this.options.filter(el => {
          const name = this.getOptionLabel(el).toLowerCase()
          if(!name) return false
          return name.includes(searchStr)
        })
      } else {
        this.filteredOptions = this.options
      }

    },
    selectOption(newOption) {
      if (!this.isOptionSelected(newOption)) {
        let newValue
        if (this.multiple) {
          newValue = [...this.value, this.getOptionValue(newOption)]
        } else {
          newValue = this.getOptionValue(newOption)
          this.isOpened = false
        }
        this.$emit('input', newValue)
      } else if (this.multiple) {
        this.$emit('input', this.value.filter(optValue => optValue !== this.getOptionValue(newOption)))
      }
      this.searchUIText = ''
      if(!this.multiple) {
        this.optionsVisibility = false
      }
      this.filteredOptions = this.options
    },
    isOptionSelected(opt) {
      let isSelected = false
        if (this.multiple) {
          isSelected = this.selectedOptions.find(option => this.getOptionValue(option) === this.getOptionValue(opt))
        } else {
          isSelected = this.getOptionValue(this.selectedOption) === this.getOptionValue(opt)
        }
      return isSelected
    },
    toggleOptions() {
      if(!this.disabled) {
        this.isOpened = !this.isOpened
        if (this.isOpened) {
          this.showOptionsOnTop = false
          this.setOptionsPosition()
          this.getfilteredOptions(this.searchUIText)
          this.optionsVisibility = true
        } else {
          this.optionsVisibility = false
        }
      }
    },
    setOptionsPosition() {
      const optionsTopPos = this.$refs.options.getBoundingClientRect().top
      this.showOptionsOnTop = optionsTopPos + this.$options.optionsMaxHeight >  window.innerHeight
    },
    hideOptions() {
      this.isOpened = false
      this.optionsVisibility = false
      this.showOptionsOnTop = false
    },
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/components/inputs/PcSelect.scss";

input.hidden {
  opacity: 0;
  position: absolute;
  width: 100%;
}
</style>
